import React from 'react';
import { FormData, FormDepth } from './types';

export type PriorityType = 'M' | 'GTF' | 'O';

export const fieldPriorities: Record<string, PriorityType> = {
  // Basic Information - Mandatory
  'handicap': 'M',
  'age': 'M',
  'height_inches': 'M',
  'years_playing': 'M',
  'dominant_hand': 'M',
  'rounds_per_year': 'GTF',

  // Swing Characteristics
  'driver_swing_data.swing_speed_driver': 'M',
  'driver_swing_data.attack_angle_driver': 'M',
  'iron_swing_data.swing_speed_7iron': 'GTF',
  'iron_swing_data.attack_angle_irons': 'GTF',

  // Ball Flight Data - Driver LM
  'launch_monitor_driver.launch_angle_driver': 'M',
  'launch_monitor_driver.spin_rate_driver': 'M',
  'launch_monitor_driver.ball_speed_driver': 'M',
  'launch_monitor_driver.driver_carry_distance': 'GTF',
  'launch_monitor_driver.face_to_path_driver': 'GTF',
  'launch_monitor_driver.spin_axis_driver': 'O',
  'launch_monitor_driver.smash_factor_driver': 'O',

  // Ball Flight Data - 7-Iron LM
  'launch_monitor_iron.launch_angle_7i': 'M',
  'launch_monitor_iron.spin_rate_7i': 'M',
  'launch_monitor_iron.ball_speed_7i': 'M',
  'launch_monitor_iron.iron_carry_distance': 'GTF',
  'launch_monitor_iron.face_to_path_7i': 'GTF',
  'launch_monitor_iron.spin_axis_7i': 'O',
  'launch_monitor_iron.smash_factor_7i': 'O',

  // Ball Flight Data - Wedge LM
  'launch_monitor_wedge.launch_angle_wedge': 'O',
  'launch_monitor_wedge.spin_rate_wedge': 'O',
  'launch_monitor_wedge.ball_speed_wedge': 'O',
  'launch_monitor_wedge.wedge_carry_distance': 'O',

  // Physical Profile
  'physical_profile.wrist_to_floor': 'O',
  'physical_profile.flexibility_rating': 'GTF',
  'physical_profile.core_strength': 'GTF',
  'physical_profile.weekly_workouts': 'GTF',
  'physical_profile.posture_type': 'O',

  // Equipment History - Satisfaction Ratings
  'equipment_history.current_driver_satisfaction': 'GTF',
  'equipment_history.current_iron_satisfaction': 'GTF',
  'equipment_history.current_wedge_satisfaction': 'GTF',

  // Current Equipment - Driver
  'equipment_history.current_clubs_driver.brand': 'GTF',
  'equipment_history.current_clubs_driver.model': 'GTF',
  'equipment_history.current_clubs_driver.loft': 'GTF',
  'equipment_history.current_clubs_driver.shaft_flex': 'M',
  'equipment_history.current_clubs_driver.shaft_model': 'GTF',
  'equipment_history.current_clubs_driver.shaft_weight': 'GTF',

  // Current Equipment - Irons
  'equipment_history.current_clubs_irons.brand': 'GTF',
  'equipment_history.current_clubs_irons.model': 'GTF',
  'equipment_history.current_clubs_irons.shaft_flex': 'M',
  'equipment_history.current_clubs_irons.shaft_model': 'GTF',
  'equipment_history.current_clubs_irons.shaft_weight': 'GTF',

  // Current Equipment - Wedges
  'equipment_history.current_clubs_wedges.brand': 'GTF',
  'equipment_history.current_clubs_wedges.model': 'GTF',
  'equipment_history.current_clubs_wedges.lofts': 'GTF',
  'equipment_history.current_clubs_wedges.grinds': 'GTF',
  'equipment_history.current_clubs_wedges.shaft_flex': 'GTF',
  'equipment_history.current_clubs_wedges.shaft_model': 'GTF',
  'equipment_history.current_clubs_wedges.shaft_weight': 'GTF',

  // All-Time Favorite Club
  'equipment_history.all_time_favorite_club.brand': 'GTF',
  'equipment_history.all_time_favorite_club.model': 'GTF',
  'equipment_history.all_time_favorite_club.club_type': 'GTF',
  'equipment_history.all_time_favorite_club.shaft_flex': 'GTF',
  'equipment_history.all_time_favorite_club.shaft_model': 'GTF',
  'equipment_history.all_time_favorite_club.shaft_weight': 'GTF',

  // Equipment History & Experience
  'equipment_history.equipment_change_frequency': 'GTF',
  'equipment_history.budget_flexibility': 'GTF',
  'equipment_history.older_brands_used_driver': 'O',
  'equipment_history.older_brands_used_irons': 'O',
  'equipment_history.older_brands_used_wedges': 'O',
  'equipment_history.custom_fitting_history': 'GTF',
  'equipment_history.launch_monitor_experience': 'O',

  // Playing Profile
  'playing_profile.rounds_per_year': 'GTF',
  'playing_profile.typical_course_type': 'GTF',
  'playing_profile.course_difficulty': 'O',
  'playing_profile.weather_conditions': 'O',
  'playing_profile.scoring_consistency': 'GTF',
  'playing_profile.performance_under_pressure': 'O',
  'playing_profile.driver_under_pressure': 'O',
  'playing_profile.irons_under_pressure': 'O',
  'playing_profile.wedges_under_pressure': 'O',

  // Preferences & Feel
  'preferences.feel_preference': 'O',
  'preferences.sound_preference': 'O',
  'preferences.visual_alignment_aids': 'O',
  'preferences.offset_preference': 'O',
  'preferences.finish_style': 'O',
  'preferences.grip_type_preference': 'O',
  'preferences.Clubs_you_are_interested_in': 'O',
  'preferences.Clubs_you_are_interested_in_looks': 'O',
  'preferences.Materials_you_are_interested_in_irons': 'GTF',
  'preferences.Materials_you_are_interested_in_drivers': 'GTF',
  'preferences.Shiftable_Lofts_drivers': 'GTF',
  'preferences.Bendable_irons': 'GTF',

  // Goals & Challenges
  'goals_challenges.primary_goal': 'M',
  'goals_challenges.target_handicap': 'GTF',
  'goals_challenges.biggest_frustration': 'M',
  'goals_challenges.purchase_timeline': 'GTF',
  'goals_challenges.practice_frequency': 'GTF',
  'goals_challenges.lesson_frequency': 'GTF',
  'goals_challenges.technology_comfort': 'O',
  'goals_challenges.in_season_off_season': 'O'
};

export const getFieldPriority = (fieldPath: string): PriorityType => {
  return fieldPriorities[fieldPath] || 'O';
};

// F2: form depth selector. 'essential' = mandatory only, 'plus' = + good-to-fill,
// 'everything' = all fields including optional/nice-to-have.
export const isVisibleAtDepth = (fieldPath: string, depth: FormDepth): boolean => {
  if (depth === 'everything') return true;
  const priority = getFieldPriority(fieldPath);
  if (depth === 'plus') return priority === 'M' || priority === 'GTF';
  return priority === 'M';
};

export const getPriorityClasses = (fieldPath: string, isEmpty: boolean = false): string => {
  const priority = getFieldPriority(fieldPath);
  const baseClasses = "mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm";

  if (priority === 'M') {
    return `${baseClasses} ${isEmpty ? 'border-red-400' : 'border-green-ink'} focus:border-green-ink focus:ring-green-ink bg-white`;
  } else if (priority === 'GTF') {
    return `${baseClasses} ${isEmpty ? 'border-yellow-400' : 'border-green-ink'} focus:border-green-ink focus:ring-green-ink bg-white`;
  } else {
    return `${baseClasses} border-gray-300 focus:border-green-ink focus:ring-green-ink bg-white`;
  }
};

export const getPriorityIndicator = (fieldPath: string): React.ReactNode => {
  const priority = getFieldPriority(fieldPath);
  if (priority === 'M') {
    return <span className="text-red-500 ml-1" title="Mandatory field">*</span>;
  } else if (priority === 'GTF') {
    return <span className="text-yellow-600 ml-1" title="Recommended field">●</span>;
  }
  return null;
};

export const countFilledFields = (obj: any): number => {
  if (!obj) return 0;
  return Object.values(obj).filter(v => v !== undefined && v !== null && v !== '').length;
};

export const countSection2LMFields = (formData: FormData): number => {
  let count = 0;
  if (formData.driver_swing_data?.club_path_driver !== undefined) count++;
  if (formData.driver_swing_data?.face_angle_driver !== undefined) count++;
  if (formData.driver_swing_data?.dynamic_loft_driver !== undefined) count++;
  if (formData.driver_swing_data?.swing_speed_driver !== undefined) count++;
  if (formData.driver_swing_data?.attack_angle_driver !== undefined) count++;
  if (formData.iron_swing_data?.swing_speed_7iron !== undefined) count++;
  if (formData.iron_swing_data?.attack_angle_irons !== undefined) count++;
  return count;
};

export const countSection2ManualFields = (formData: FormData): number => {
  return countSection2LMFields(formData);
};

export const countSection3LMFields = (formData: FormData): number => {
  let count = 0;
  if (formData.launch_monitor_driver?.face_to_path_driver !== undefined) count++;
  if (formData.launch_monitor_driver?.spin_axis_driver !== undefined) count++;
  if (formData.launch_monitor_driver?.dispersion_std_dev_driver !== undefined) count++;
  if (formData.launch_monitor_driver?.shot_count_driver !== undefined) count++;
  count += countFilledFields(formData.launch_monitor_driver) - count;
  count += countFilledFields(formData.launch_monitor_iron);
  count += countFilledFields(formData.launch_monitor_wedge);
  return count;
};

export const countSection3ManualFields = (formData: FormData): number => {
  let count = 0;
  count += countFilledFields(formData.driver_ball_flight);
  count += countFilledFields(formData.iron_data);
  count += countFilledFields(formData.wedge_data);
  return count;
};

export const getFieldValue = (formData: FormData, fieldPath: string): any => {
  const parts = fieldPath.split('.');
  let value: any = formData;
  for (const part of parts) {
    if (value && typeof value === 'object') {
      value = value[part];
    } else {
      return undefined;
    }
  }
  return value;
};
